/**
 * cloudTime.js — device-timezone <-> UTC conversion for cloud playback.
 *
 * The cloud DB (JSEvent) stores event timestamps in UTC, but the viewer's whole
 * playback time-model is tz-naive wall-clock in the DEVICE's local timezone (the
 * user picks a local time in the entry modal; the NVR "now" comes from the camera
 * metadata clock, which is device-local). So the cloud events fetch must convert
 * the user's local window -> UTC for the `/ve/playbackevents` query, and convert
 * each returned UTC event timestamp -> device-local so its offset lines up with
 * the local window start (mcpbClips math). This is the Java MCEventModel.localToUTC
 * path that mcpbTime.js explicitly defers ("CloudVS UTC<->local conversion is a
 * Phase 4a concern ... NOT done here").
 *
 * Pure + DST-correct (via Intl timeZone), so it is unit-testable without a clock.
 */

const TS_RE = /^(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})$/;
const pad2 = (n) => ('0' + n).slice(-2);

// 'YYYYMMDDHHMMSS' -> epoch ms treating the digits as UTC wall-clock. NaN if malformed.
const tsToMs = (ts) => {
  const m = TS_RE.exec(ts || '');
  return m ? Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]) : NaN;
};

// epoch ms -> 'YYYYMMDDHHMMSS' read as UTC wall-clock.
const msToTs = (ms) => {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}${pad2(d.getUTCHours())}${pad2(d.getUTCMinutes())}${pad2(d.getUTCSeconds())}`;
};

/**
 * How far ahead of UTC the tz is at a given UTC instant, in ms (DST-correct).
 * e.g. America/Chicago in July -> -5h -> returns -18000000.
 */
const tzOffsetMs = (utcMs, tz) => {
  try {
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz, hourCycle: 'h23',
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
    const p = {};
    for (const part of dtf.formatToParts(new Date(utcMs))) {
      if (part.type !== 'literal') p[part.type] = part.value;
    }
    const asUtc = Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute, +p.second);
    return asUtc - utcMs;
  } catch (e) {
    return 0; // unknown tz -> treat as UTC (no shift)
  }
};

/**
 * Device-local wall-clock 'YYYYMMDDHHMMSS' -> UTC 'YYYYMMDDHHMMSS'.
 * @param {string} localTs
 * @param {string} tz - IANA zone (e.g. 'America/Chicago'); falsy/unknown -> unchanged
 */
export const localToUtc = (localTs, tz) => {
  const ms = tsToMs(localTs);
  if (isNaN(ms) || !tz) return localTs;
  // Offset at the naive instant is a good approximation of the offset at the real
  // instant (only differs within the ~1h DST-transition overlap — acceptable for
  // a playback window query).
  const offset = tzOffsetMs(ms, tz);
  return msToTs(ms - offset);
};

/**
 * UTC 'YYYYMMDDHHMMSS' -> device-local wall-clock 'YYYYMMDDHHMMSS'.
 * @param {string} utcTs
 * @param {string} tz - IANA zone; falsy/unknown -> unchanged
 */
export const utcToLocal = (utcTs, tz) => {
  const ms = tsToMs(utcTs);
  if (isNaN(ms) || !tz) return utcTs;
  const offset = tzOffsetMs(ms, tz);
  return msToTs(ms + offset);
};

/**
 * The site tz's UTC offset as an ISO/MySQL '+HH:MM' / '-HH:MM' string, evaluated at a given instant
 * (DST-correct via tzOffsetMs). Used to bucket LEGACY cloud events (stored UTC) to LOCAL calendar days
 * in SQL WITHOUT the server's named-timezone tables — the cloud RDS has none loaded, so
 * CONVERT_TZ(ts,'UTC','America/Chicago') returns NULL; the offset form CONVERT_TZ(ts,'+00:00','-05:00')
 * always works (card 86bbqvr6f presence dots). One offset is used per queried month, so the ≤2
 * DST-transition months can misplace a midnight-adjacent event's dot by an hour — immaterial for a
 * day-dot, and dragon (device-local storage) doesn't use this path at all.
 * @param {string} tz - IANA zone (e.g. 'America/Chicago'); falsy/unknown -> '+00:00' (no shift)
 * @param {string} atTs - 'YYYYMMDDHHMMSS' instant to evaluate the offset at (e.g. the viewed month start)
 * @returns {string} e.g. '-05:00', '+00:00'
 */
export const tzOffsetString = (tz, atTs) => {
  const ms = tsToMs(atTs);
  if (isNaN(ms) || !tz) return '+00:00';
  const offMs = tzOffsetMs(ms, tz);            // ahead-of-UTC in ms (Chicago summer -> -18000000)
  const sign = offMs < 0 ? '-' : '+';
  const absMin = Math.round(Math.abs(offMs) / 60000);
  return `${sign}${pad2(Math.floor(absMin / 60))}:${pad2(absMin % 60)}`;
};

/**
 * Format an instant in a site's IANA zone for a clock display (#86bbm5p92). Returns the wall-clock
 * parts in that zone plus the short zone label (e.g. 'CDT'). A falsy/unknown tz falls back to the
 * BROWSER's local zone with an empty label — the decided behavior for an unset JSCustomerProject
 * .sTimezone (no regression; a Setup nudge prompts the admin to set it). Pure + DST-correct via Intl.
 * @param {Date} date - the instant to format (defaults to now if invalid)
 * @param {string} tz - IANA zone (e.g. 'America/Chicago'); falsy/unknown -> browser-local, abbrev ''
 * @returns {{date:string,time:string,abbrev:string}} date 'MM/DD/YYYY', time 'HH:MM:SS' (24h), abbrev e.g. 'CDT'
 */
export const formatClockInZone = (date, tz) => {
  const d = (date instanceof Date && !isNaN(date.getTime())) ? date : new Date();
  // hourCycle:'h23' (0-23), NOT hour12:false: the latter makes some engines emit hour '24' at midnight,
  // and the date parts alongside it are the PREVIOUS day, so fixing only the hour showed the wrong day
  // for one second (#86bbm5p92 review L4). h23 yields '00' at midnight with the correct date.
  const base = { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23' };
  try {
    const opts = tz ? { ...base, timeZone: tz, timeZoneName: 'short' } : { ...base, timeZoneName: 'short' };
    const parts = {};
    for (const p of new Intl.DateTimeFormat('en-US', opts).formatToParts(d)) parts[p.type] = p.value;
    const hh = parts.hour === '24' ? '00' : parts.hour;   // belt-and-suspenders for a non-compliant engine
    return {
      date: `${parts.month}/${parts.day}/${parts.year}`,
      time: `${hh}:${parts.minute}:${parts.second}`,
      abbrev: tz ? (parts.timeZoneName || '') : '',        // only claim a zone when one is configured
    };
  } catch (e) {
    // Unknown/invalid tz -> browser-local, no label.
    const p2 = (n) => ('0' + n).slice(-2);
    return {
      date: `${p2(d.getMonth() + 1)}/${p2(d.getDate())}/${d.getFullYear()}`,
      time: `${p2(d.getHours())}:${p2(d.getMinutes())}:${p2(d.getSeconds())}`,
      abbrev: '',
    };
  }
};
